/**
 * @jsxRuntime classic
 * @jsxFrag jsx
 * @jsx jsx
 */
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
import { css, cssMap, jsx } from '@compiled/react';

import SVG from '@atlaskit/icon/svg';
import type { SVGProps } from '@atlaskit/icon/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

const sizeStyles = cssMap({
	small: {
		width: '16px',
		height: '16px',
	},
	medium: {
		width: '24px',
		height: '24px',
	},
	large: {
		width: '32px',
		height: '32px',
	},
	xlarge: {
		width: '48px',
		height: '48px',
	},
});

const svgStyles = css({
	fill: token('elevation.surface'),
	overflow: 'hidden',
	pointerEvents: 'none',
});

const rovoIconPath = (
	<>
		<path
			d="M10.7601 0.338448C11.5933 -0.121106 12.6101 -0.112663 13.4365 0.363775L19.9352 4.11317L21.4762 5.0023C22.3177 5.4874 22.8385 6.3874 22.8385 7.35696V16.6341C22.8385 17.6075 22.3208 18.5036 21.4769 18.9884L15.4334 22.4752C15.4466 22.438 15.4592 22.4006 15.4713 22.363C15.5834 22.0154 15.6429 21.6481 15.6429 21.2727V11.9955C15.6429 10.7211 14.9646 9.5458 13.8581 8.91002L12.7419 8.26625L9.35306 6.31196V2.71838C9.35306 2.43521 9.39687 2.1586 9.47978 1.89674C9.68356 1.26299 10.117 0.714353 10.7101 0.371867L10.7119 0.37083C10.7288 0.361022 10.745 0.350144 10.7601 0.338448Z"
			fill="#FCA700"
		/>
		<path
			d="M8.71417 1.52478L2.67066 5.01159C1.82674 5.49643 1.30908 6.39246 1.30908 7.36586V16.643C1.30908 17.6126 1.82979 18.5125 2.67121 18.9976L4.10412 19.8243L10.7111 23.6362C11.5374 24.1126 12.5543 24.1211 13.3875 23.6616C13.4026 23.6499 13.4186 23.639 13.4356 23.6292C14.0293 23.287 14.4634 22.7381 14.6675 22.1041C14.7506 21.842 14.7945 21.5651 14.7945 21.2816V17.688L11.2974 15.6713L10.2897 15.0901C9.18348 14.4543 8.50468 13.2788 8.50468 12.0044V2.72728C8.50468 2.35237 8.56398 1.98558 8.67586 1.63842C8.68799 1.60029 8.70078 1.56241 8.71417 1.52478Z"
			fill="#FCA700"
		/>
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M11.2974 15.6713L4.10412 19.8243L2.67121 18.9976C1.82979 18.5125 1.30908 17.6126 1.30908 16.643V7.36586C1.30908 6.39246 1.82674 5.49643 2.67066 5.01159L8.71417 1.52478C8.70078 1.56241 8.68799 1.60029 8.67586 1.63842C8.56398 1.98558 8.50468 2.35237 8.50468 2.72728V12.0044C8.50468 13.2788 9.18348 14.4543 10.2897 15.0901L11.2974 15.6713Z"
			fill="#1868DB"
		/>
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M12.7419 8.26625L9.35306 6.31196V2.71838C9.35306 2.43521 9.39687 2.15859 9.47978 1.89674C9.68356 1.26299 10.117 0.714353 10.7101 0.371867L10.7119 0.37083C10.7288 0.361022 10.745 0.350144 10.7601 0.338447C11.5933 -0.121106 12.6101 -0.112663 13.4365 0.363775L19.9352 4.11317L12.7419 8.26625Z"
			fill="#FCA700"
		/>
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M21.4762 5.00229L19.9352 4.11316L12.7419 8.26624L13.8581 8.91001C14.9646 9.54579 15.6429 10.721 15.6429 11.9955V21.2727C15.6429 21.6481 15.5834 22.0154 15.4713 22.363C15.4592 22.4006 15.4466 22.438 15.4334 22.4752L21.4769 18.9884C22.3208 18.5036 22.8385 17.6075 22.8385 16.6341V7.35695C22.8385 6.38739 22.3177 5.48739 21.4762 5.00229Z"
			fill="#AF59E0"
		/>
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M11.2975 15.6713L4.10413 19.8244L10.7111 23.6362C11.5375 24.1126 12.5544 24.1211 13.3875 23.6616C13.4026 23.6499 13.4186 23.639 13.4356 23.6292C14.0293 23.287 14.4634 22.7381 14.6675 22.1041C14.7506 21.842 14.7945 21.5651 14.7945 21.2816V17.688L11.2975 15.6713Z"
			fill="#6A9A23"
		/>
	</>
);

const RovoIcon = (props: SVGProps) => {
	const { primaryColor, size = 'small', label } = props;

	if (fg('platform-custom-icon-migration')) {
		return (
			<svg
				viewBox="0 0 24 24"
				style={{
					color: primaryColor,
				}}
				css={[svgStyles, sizeStyles[size]]}
				aria-label={label || undefined}
				role={label ? 'img' : 'presentation'}
			>
				{rovoIconPath}
			</svg>
		);
	}

	return (
		<SVG primaryColor={primaryColor} size={size} label={label}>
			{rovoIconPath}
		</SVG>
	);
};

const rovoIconJiraPath = (
	<g transform="translate(4, 4)">
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M7.53094 10.4476L2.73541 13.2163L1.78022 12.6652C1.21927 12.3418 0.872135 11.7418 0.872135 11.0954V4.91066C0.872135 4.26173 1.21724 3.66437 1.77985 3.34114L5.80885 1.01662C5.79992 1.0417 5.7914 1.06696 5.78331 1.09238C5.70872 1.32382 5.66919 1.56834 5.66919 1.81828V8.00304C5.66919 8.8526 6.12173 9.63628 6.85921 10.0602L7.53094 10.4476Z"
			fill="#357DE8"
		/>
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M7.53076 10.4476L2.73523 13.2163L7.13984 15.7575C7.69076 16.0751 8.36871 16.0808 8.92411 15.7744C8.93417 15.7666 8.94488 15.7594 8.95621 15.7529C9.35198 15.5247 9.6414 15.1588 9.77748 14.7361C9.83287 14.5614 9.86214 14.3768 9.86214 14.1878V11.7921L7.53076 10.4476Z"
			fill="#82B536"
		/>
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M14.3184 3.33501L13.291 2.74227L8.49549 5.51098L9.23969 5.94015C9.97729 6.36401 10.4295 7.14752 10.4295 7.99717V14.1819C10.4295 14.4322 10.3899 14.6771 10.3151 14.9088C10.3071 14.9339 10.2987 14.9588 10.2899 14.9836L14.3189 12.6591C14.8815 12.3358 15.2266 11.7385 15.2266 11.0895V4.90479C15.2266 4.25842 14.8794 3.65842 14.3184 3.33501Z"
			fill="#BF63F3"
		/>
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M8.49384 5.51082L6.23466 4.20796V1.81225C6.23466 1.62347 6.26387 1.43906 6.31913 1.26449C6.455 0.841993 6.74398 0.476235 7.13931 0.247911L7.14051 0.247219C7.15185 0.240681 7.16263 0.233429 7.17269 0.225631C7.72811 -0.0807372 8.40603 -0.0751089 8.95696 0.242516L13.2894 2.74211L8.49384 5.51082Z"
			fill="#FCA700"
		/>
	</g>
);

const RovoIconJira = (props: SVGProps) => {
	const { primaryColor, size = 'large', label } = props;

	if (fg('platform-custom-icon-migration')) {
		return (
			<svg
				viewBox="0 0 24 24"
				style={{
					color: primaryColor,
				}}
				css={[svgStyles, sizeStyles[size]]}
				aria-label={label || undefined}
				role={label ? 'img' : 'presentation'}
			>
				{rovoIconJiraPath}
			</svg>
		);
	}

	return (
		<SVG primaryColor={primaryColor} size={size} label={label}>
			{rovoIconJiraPath}
		</SVG>
	);
};
export default RovoIcon;
export { RovoIconJira };
